import { useState, useEffect, useLayoutEffect, useRef, useCallback, type ReactNode } from 'react'
import { Calendar, Clock } from 'lucide-react'
import { Interactive, refreshInteractiveBounds } from '@highlight/ui'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { CalendarGrid } from './nested/calendar-grid'
import { PresetList } from './nested/preset-list'
import { TimeSuggestions } from './nested/time-suggestions'
import { DateField } from './nested/date-field'
import { TimeField } from './nested/time-field'

/** Each panel remounts its tracker so overlay hit-testing covers the new height immediately. */
function InteractivePanel({ children }: { children: ReactNode }) {
  return (
    <div className="relative">
      <Interactive>{children}</Interactive>
    </div>
  )
}

type ActivePanel = 'presets' | 'date' | 'time'

interface ReminderDatePickerProps {
  value?: Date | null
  onChange: (date: Date | null) => void
}

function getTimeString(date: Date): string {
  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
}

function combineDateTime(date: Date, time: string): Date {
  const [hours, minutes] = time.split(':').map(Number)
  const combined = new Date(date)
  combined.setHours(hours, minutes, 0, 0)
  return combined
}

export function ReminderDatePicker({ value, onChange }: ReminderDatePickerProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(value ?? undefined)
  const [selectedTime, setSelectedTime] = useState<string | undefined>(value ? getTimeString(value) : undefined)
  const [activePanel, setActivePanel] = useState<ActivePanel>('presets')
  const lastValueMs = useRef<number | null>(value ? value.getTime() : null)

  useEffect(() => {
    const ms = value ? value.getTime() : null
    if (ms === lastValueMs.current) return
    lastValueMs.current = ms
    if (value) {
      setSelectedDate(value)
      setSelectedTime(getTimeString(value))
    }
  }, [value])

  const handlePresetSelect = (date: Date, label: string) => {
    setSelectedDate(date)
    setSelectedTime(getTimeString(date))
    lastValueMs.current = date.getTime()
    onChange(date)
    setTimeout(() => setActivePanel('time'), 120)
    AmplitudeRenderer.trackEvent('Reminder Action', { type: 'Select Date Preset', presetLabel: label })
  }

  const handleClear = () => {
    if (blurTimeout.current) clearTimeout(blurTimeout.current)
    setSelectedDate(undefined)
    setSelectedTime(undefined)
    lastValueMs.current = null
    onChange(null)
    AmplitudeRenderer.trackEvent('Reminder Action', { type: 'Remove Due Date' })
  }

  const handleDaySelect = (day: Date | undefined) => {
    if (!day) return
    if (blurTimeout.current) clearTimeout(blurTimeout.current)
    setSelectedDate(day)
    const time = selectedTime ?? '09:00'
    const combined = combineDateTime(day, time)
    lastValueMs.current = combined.getTime()
    onChange(combined)
    setTimeout(() => setActivePanel('time'), 120)
  }

  const handleDateFieldChange = (date: Date) => {
    setSelectedDate(date)
    const time = selectedTime ?? '09:00'
    const combined = combineDateTime(date, time)
    lastValueMs.current = combined.getTime()
    onChange(combined)
  }

  const handleTimeSelect = (time: string) => {
    if (blurTimeout.current) clearTimeout(blurTimeout.current)
    setSelectedTime(time)
    const date = selectedDate ?? new Date()
    if (!selectedDate) setSelectedDate(date)
    const combined = combineDateTime(date, time)
    lastValueMs.current = combined.getTime()
    onChange(combined)
  }

  const handleTimeFieldChange = (time: string) => {
    setSelectedTime(time)
    const date = selectedDate ?? new Date()
    if (!selectedDate) setSelectedDate(date)
    const combined = combineDateTime(date, time)
    lastValueMs.current = combined.getTime()
    onChange(combined)
  }

  // isInteracting is set true on mousedown (which fires before blur), so the blur
  // handler skips the reset-to-presets timeout when the user clicks inside the picker.
  // The timeout is still used for keyboard focus-out (tab, click-outside).
  const isInteracting = useRef(false)
  const blurTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const resetPanelOnBlur = useCallback(() => {
    if (isInteracting.current) return
    if (blurTimeout.current) clearTimeout(blurTimeout.current)
    blurTimeout.current = setTimeout(() => setActivePanel('presets'), 150)
  }, [])

  useLayoutEffect(() => {
    refreshInteractiveBounds()
  }, [activePanel])

  return (
    <div
      className="flex flex-col gap-3 py-2"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => {
        e.stopPropagation()
        isInteracting.current = true
      }}
      onMouseUp={() => {
        isInteracting.current = false
      }}
    >
      {/* Header row: Date [field]  Time [field] */}
      <div className="flex items-center justify-center gap-2 px-3">
        <Calendar
          className="text-foreground-secondary hover:text-foreground size-3.5 shrink-0 cursor-pointer transition-colors"
          onClick={() => {
            if (blurTimeout.current) clearTimeout(blurTimeout.current)
            setActivePanel('date')
          }}
        />
        <DateField
          value={selectedDate}
          onFocus={() => {
            if (blurTimeout.current) clearTimeout(blurTimeout.current)
            setActivePanel('date')
          }}
          onBlur={resetPanelOnBlur}
          onChange={handleDateFieldChange}
        />
        <Clock
          className="text-foreground-secondary hover:text-foreground size-3.5 shrink-0 cursor-pointer transition-colors"
          onClick={() => {
            if (blurTimeout.current) clearTimeout(blurTimeout.current)
            setActivePanel('time')
          }}
        />
        <TimeField
          value={selectedTime}
          onFocus={() => {
            if (blurTimeout.current) clearTimeout(blurTimeout.current)
            setActivePanel('time')
          }}
          onBlur={resetPanelOnBlur}
          onChange={handleTimeFieldChange}
        />
      </div>

      <div className="mx-3 border-t" />

      {activePanel === 'presets' && (
        <InteractivePanel>
          {/* Keyed off the picker's own selection, not `value`: an uncommitted
              pick is still a due date the user can take back off. */}
          <PresetList onSelect={handlePresetSelect} onClear={selectedDate ? handleClear : undefined} />
        </InteractivePanel>
      )}
      {activePanel === 'date' && (
        <InteractivePanel>
          <CalendarGrid selected={selectedDate} onSelect={handleDaySelect} />
        </InteractivePanel>
      )}
      {activePanel === 'time' && (
        <InteractivePanel>
          <TimeSuggestions selectedTime={selectedTime} onSelect={handleTimeSelect} />
        </InteractivePanel>
      )}
    </div>
  )
}
